import { Injectable } from "@angular/core";
import { MessageService } from "primeng/api";

export interface ToastMessage {
  severity: "success" | "info" | "warn" | "error";
  summary: string;
  detail?: string;
  life?: number;
  closable?: boolean;
}

@Injectable({
  providedIn: "root"
})
export class ToastService {
  
  constructor(private messageService: MessageService) {}

  /**
   * Show a toast notification
   * @param message The toast message configuration
   */
  show(message: ToastMessage): void {
    this.messageService.add({
      severity: message.severity,
      summary: message.summary,
      detail: message.detail,
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      life: message.life || 5000,
      closable: message.closable !== false
    });
  }

  /**
   * Show a success toast
   * @param summary The main message
   * @param detail Optional detail message
   * @param life Optional duration in milliseconds
   */
  success(summary: string, detail?: string, life?: number): void {
    this.show({
      severity: "success",
      summary,
      detail,
      life
    });
  }

  /**
   * Show an error toast
   * @param summary The main message
   * @param detail Optional detail message
   * @param life Optional duration in milliseconds
   */
  error(summary: string, detail?: string, life?: number): void {
    this.show({
      severity: "error",
      summary,
      detail,
      life
    });
  }

  /**
   * Show a warning toast
   * @param summary The main message
   * @param detail Optional detail message
   * @param life Optional duration in milliseconds
   */
  warn(summary: string, detail?: string, life?: number): void {
    this.show({
      severity: "warn",
      summary,
      detail,
      life
    });
  }

  /**
   * Show an info toast
   * @param summary The main message
   * @param detail Optional detail message
   * @param life Optional duration in milliseconds
   */
  info(summary: string, detail?: string, life?: number): void {
    this.show({
      severity: "info",
      summary,
      detail,
      life
    });
  }

  /**
   * Clear all toast messages
   */
  clear(): void {
    this.messageService.clear();
  }
} 